import {
  Album,
  Artist,
  CommunityPost,
  LabEvent,
  LabLiveTraining,
  Setlist,
  Song,
  StoreItemTypeEnum,
  User
} from '../__generated__/graphql'

const albumPath = (album: Album): string => {
  return `${albumsPath(album.primaryArtist)}/${album.id}`
}

const albumsPath = (artist: Artist): string => {
  return `/artists/${artist.id}/albums`
}

const artistsPath = (params: any = {}): string => {
  const query = new URLSearchParams(params).toString()
  return query.length > 0 ? `/artists?${query.toString()}` : `/artists`
}

const artistPath = (artist: Artist): string => {
  return `/artists/${artist.id}`
}
const artistSongsPath = (artist: Artist): string => {
  return `${artistPath(artist)}/songs`
}

/** Route segment for a song page (`419-leeland--way-maker` or `419`). */
const songRouteParam = (song: Pick<Song, 'id' | 'slug'>): string =>
  song.slug || String(song.id)

const songPath = (song: Pick<Song, 'id' | 'slug'>): string =>
  `/songs/${songRouteParam(song)}`

/** Extract numeric song id from route param (`419` or `419-leeland--way-maker`). */
const parseSongIdFromRouteParam = (param: string): number | null => {
  if (!param) return null

  const match = param.match(/^(\d+)(?:-|$)/)
  return match ? Number(match[1]) : null
}

const songsPath = (params: any = {}): string => {
  const query = new URLSearchParams(params).toString()
  return query.length > 0 ? `/songs?${query.toString()}` : `/songs`
}

const downloadSetlistEventsPath = (setlist: Setlist) => {
  return `${process.env.NEXT_PUBLIC_OLD_URL}/setlists/${setlist.id}/download_schedule`
}
const downloadEventSchedulePath = (event: LabEvent) => {
  return `${process.env.NEXT_PUBLIC_OLD_URL}/lab_events/${event.id}/download_schedule`
}
const downloadLiveTrainingSchedulePath = (liveTraining: LabLiveTraining) => {
  return `${process.env.NEXT_PUBLIC_OLD_URL}/lab_live_trainings/${liveTraining.id}/download_schedule`
}

const storeUrl = () => {
  return '/store'
}

const storeSongsPath = (
  params: { q?: string; orderBy?: string; favorite?: string } = {
    q: '',
    orderBy: '',
    favorite: 'false'
  }
): string => {
  const query = new URLSearchParams(params).toString()
  const url =
    query.length > 0 ? `${storeUrl()}/songs?${query}` : `${storeUrl()}/songs`
  return url
}

const storeSongPath = (
  song: Pick<Song, 'id'>,
  tab?: StoreItemTypeEnum
): string => {
  return `/store/songs/${song.id}?tab=${tab || 'multi_tracks'}`
}

const topMultitracksPath = () => `${storeUrl()}/songs?sort=popular`

const topKeysPath = () => `${storeUrl()}/songs`

const wistiaVideoPath = (path: string): string => {
  return `https://home.wistia.com/medias/${path}`
}

const labPath = () => '/lab'

const labSignInPath = (redirectTo?: string) => {
  const path = labPath() + '/sign_in'
  return redirectTo
    ? `${path}?redirect=${encodeURIComponent(redirectTo)}`
    : path
}

const labChatPath = () => labPath() + '/chat_assist'

const labChatThreadPath = (threadId: number, messageId?: string) => {
  const labPath = labChatPath() + '/' + threadId
  return messageId ? labPath + '?message=' + messageId : labPath
}

const labChatSavedThreadsPath = () => labChatPath() + '/saved'

const labSignupPath = () => labPath() + '/signup'

const labCheckoutPath = () => labSignupPath() + '/checkout'

const labEnrollmentClosedPath = () => labSignupPath() + '/enrollment_closed'

const labBonusPath = () => labPath() + '/bonus'

const labBonusSuccessPath = () => labBonusPath() + '/success'

const withWelcomeQuery = (path: string, welcome: boolean) =>
  welcome ? `${path}?welcome` : path

const labDealsPath = () => labPath() + '/deals'

const labTrainingPath = () => labPath() + '/training'

const labEventsPath = () => labTrainingPath() + '/events'

const labEventPath = (eventId: number) => labEventsPath() + '?event=' + eventId

const labCoursesPath = () => labTrainingPath() + '/courses'

const labBookmarksPath = () => labTrainingPath() + '/bookmarks'

const labCoursePath = (courseId: number) => labCoursesPath() + '/' + courseId

const labLessonPath = (courseId: number, lessonId = 0) =>
  labCoursesPath() + '/' + courseId + '?lesson=' + lessonId

const labLiveTrainingsPath = () => labTrainingPath() + '/live_training'

const labLiveTrainingPath = (liveTrainingId: number) =>
  labLiveTrainingsPath() + '?live_training=' + liveTrainingId

const labLiveTrainingReplayPath = (liveTrainingId: number) =>
  labLiveTrainingsPath() + '/replay/' + liveTrainingId

const labCommunityPath = () => labPath() + '/community'

const labCommunityMyPostsPath = () => labCommunityPath() + '/my_posts'
const labCommunitySavedPostsPath = () => labCommunityPath() + '/saved_posts'
const labCommunityFollowedPostsPath = () =>
  labCommunityPath() + '/followed_posts'
const labCommunityMyCommentsPath = () => labCommunityPath() + '/my_comments'
const labCommunityLeaderboardPath = () => labCommunityPath() + '/leaderboard'

const labCommunityTopicPath = (topicId: number): string =>
  labCommunityPath() + '/' + 'topics/' + topicId
const labCommunityAllTopicsPath = (): string =>
  labCommunityPath() + '/' + 'topics/all'

const labCommunityPostPath = (post: CommunityPost): string =>
  labCommunityTopicPath(post.category.id) + '/' + 'posts/' + post.id

const labCommunityCommentPath = (
  post: CommunityPost,
  commentId: number
): string => labCommunityPostPath(post) + '#' + `comment-${commentId}`

const labCommunityMembersPath = (): string =>
  labCommunityPath() + '/' + 'members'

const labCommunityMemberPath = (member: User): string =>
  labCommunityMembersPath() + '/' + member?.id

const labNotificationSettingsPath = (): string =>
  labPath() + '/' + 'notification_settings'

const labCommunityGuidelinesPath = (): string =>
  labCommunityPath() + '/' + 'guidelines'

export {
  songPath,
  songRouteParam,
  parseSongIdFromRouteParam,
  artistPath,
  artistSongsPath,
  artistsPath,
  albumPath,
  albumsPath,
  songsPath,
  downloadSetlistEventsPath,
  downloadEventSchedulePath,
  storeUrl,
  storeSongsPath,
  storeSongPath,
  topMultitracksPath,
  topKeysPath,
  wistiaVideoPath,
  labPath,
  labSignInPath,
  labBonusPath,
  labBonusSuccessPath,
  labChatPath,
  labChatThreadPath,
  labChatSavedThreadsPath,
  labSignupPath,
  labCheckoutPath,
  labEnrollmentClosedPath,
  withWelcomeQuery,
  labDealsPath,
  labTrainingPath,
  labEventsPath,
  labEventPath,
  labCoursesPath,
  labCoursePath,
  labLessonPath,
  labLiveTrainingsPath,
  labLiveTrainingPath,
  labCommunityPath,
  labCommunityAllTopicsPath,
  labCommunityMyPostsPath,
  labCommunitySavedPostsPath,
  labCommunityFollowedPostsPath,
  labCommunityMyCommentsPath,
  labCommunityLeaderboardPath,
  labCommunityTopicPath,
  labCommunityPostPath,
  labCommunityCommentPath,
  labCommunityMembersPath,
  labCommunityMemberPath,
  labNotificationSettingsPath,
  labCommunityGuidelinesPath,
  downloadLiveTrainingSchedulePath,
  labBookmarksPath,
  labLiveTrainingReplayPath
}
